import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ImageLightboxOpenOptions, ImageLightboxState } from '@core/models';

@Injectable({
  providedIn: 'root',
})
export class ImageLightboxService {
  private readonly document = inject(DOCUMENT);
  private readonly stateSubject = new BehaviorSubject<ImageLightboxState | null>(null);
  readonly lightbox$ = this.stateSubject.asObservable();

  open(options: ImageLightboxOpenOptions): void {
    if (!options.photos.length) return;

    const currentIndex = this.clampIndex(options.initialIndex ?? 0, options.photos.length);

    this.stateSubject.next({
      visible: true,
      title: options.title,
      photos: options.photos,
      currentIndex,
    });
    this.document.body.style.overflow = 'hidden';
  }

  close(): void {
    this.stateSubject.next(null);
    this.document.body.style.overflow = '';
  }

  next(): void {
    this.updateIndex((index, length) => (index + 1) % length);
  }

  previous(): void {
    this.updateIndex((index, length) => (index - 1 + length) % length);
  }

  goTo(index: number): void {
    this.updateIndex((_, length) => this.clampIndex(index, length));
  }

  private updateIndex(next: (index: number, length: number) => number): void {
    const current = this.stateSubject.getValue();
    if (!current) return;

    this.stateSubject.next({
      ...current,
      currentIndex: next(current.currentIndex, current.photos.length),
    });
  }

  private clampIndex(index: number, length: number): number {
    return Math.min(Math.max(index, 0), length - 1);
  }
}
